export class ArrayHelper {
  /**
   * Add one item to array
   *
   * @param data array
   * @param item item to add
   * @returns array with new item
   */
  static addOne<T>(data: T[], item: T): T[] {
    return [...data, item];
  }

  /**
   * Update one item in array using id as comparator
   *
   * @param data array
   * @param item item to update
   * @returns array with updated item
   */
  static updateOne<T extends { id?: number | string }>(data: T[], item: T): T[];
  /**
   * Update one item in array using provided comparator fn
   *
   * @param data array
   * @param item item to update
   * @param comparatorFn
   * @returns array with updated item
   */
  static updateOne<T extends Record<string, any>>(data: T[], item: T, comparatorFn: (a: T, b: T) => boolean): T[];
  static updateOne<T extends Record<string, any>>(data: T[], item: T, comparatorFn?: (a: T, b: T) => boolean): T[] {
    return data.map((d: T) => {
      if (!(comparatorFn ?? ArrayHelper.defaultCompareFn)(d, item)) {
        return d;
      }
      return item;
    });
  }

  /**
   * Add Or Update one item in array
   *
   * @param data array
   * @param item item to add or update
   * @returns array with updated or added item
   */
  static addOrUpdateOne<T extends { id?: number | string }>(data: T[], item: T): T[] {
    if (this.findById(data, item)) {
      return ArrayHelper.updateOne(data, item);
    }
    return ArrayHelper.addOne(data, item);
  }

  /**
   * Remove one item in array
   *
   * @param data array
   * @param index index of item to remove
   * @returns array without deleted item
   */
  static removeOne<T>(data: T[], index: number): T[] {
    return [...data.slice(0, index), ...data.slice(index + 1)];
  }

  /**
   * Filter duplicates in array
   *
   * @param data array
   * @returns array without duplicates
   */
  static filterDuplicates<T extends { id?: number | string }>(data: T[]): T[] {
    return data.filter((item: T, index: number, self: T[]) => self.findIndex((i: T) => i.id === item.id) === index);
  }

  /**
   * Find one item in array by Id
   *
   * @param data array
   * @param item item to find
   * @returns founded item if present or undefined
   */
  static findById<T extends { id?: number | string }>(data: T[], item: T): T | undefined {
    return data.find((dataItem: T) => dataItem?.id === item?.id);
  }

  /**
   * Find item index in array by Id
   *
   * @param data array
   * @param item item to find
   * @returns founded index if present or -1
   */
  static findIndexById<T extends { id?: number | string }>(data: T[], item: T): number {
    return data.findIndex((dataItem: T) => dataItem?.id === item?.id);
  }

  static numberToIterable(value: number): number[] {
    return Array(value)
      .fill(0)
      .map((_, i) => i + 1);
  }

  private static defaultCompareFn<T extends { id?: number | string }>(a: T, b: T): boolean {
    return a?.id === b?.id;
  }
}
